import type { EstadoDeSuscripcion, ImpuestoDeCobro, PlanALaVenta, ResultadoDeCaptura, SuscripcionVigente } from '../api/tipos'
import { dolares } from '../socios/dinero'

/**
 * Lo que la pantalla «Créditos y plan» calcula para pintar los cobros de Wixy (CLAUDE.md
 * §31), separado de React para poder probarlo. Nada de esto decide un precio: los importes
 * los pone el núcleo y aquí solo se escriben.
 */

// --- Dinero -------------------------------------------------------------------------------

/** `1900` → `$19`; `1520` → `$15,20`. Un precio redondo no lleva los ceros. */
export const precioRedondo = (centimos: number): string => dolares(centimos).replace(/,00$/, '')

/** ¿Se enseña el precio sin el impuesto y con «+ impuesto» al lado? Solo si va aparte y hay algo que sumar. */
export const impuestoAparte = (impuesto: ImpuestoDeCobro): boolean => !impuesto.incluido && impuesto.porcentaje > 0

/**
 * El precio de catálogo de un importe que **ya llega con el impuesto sumado** (el núcleo cobra
 * `precio × (1 + impuesto)` cuando el impuesto va aparte). Deshacer la cuenta es exacto: el núcleo
 * redondea a céntimos una vez, y ese medio céntimo, dividido por 1,18, no llega a cambiar
 * el redondeo de vuelta.
 */
export const sinImpuesto = (centimos: number, impuesto: ImpuestoDeCobro): number =>
  impuestoAparte(impuesto) ? Math.round((centimos * 100) / (100 + impuesto.porcentaje)) : centimos

export type PrecioVisible = {
  /** Lo grande: el precio de catálogo. */
  importe: string
  /** Si al lado va «+ impuesto». */
  masImpuesto: boolean
  /** Lo que de verdad cobra PayPal, con el impuesto, cuando va aparte; si no, `null`. */
  total: null | string
}

export const precioVisible = (centimos: number, impuesto: ImpuestoDeCobro): PrecioVisible =>
  impuestoAparte(impuesto)
    ? { importe: precioRedondo(sinImpuesto(centimos, impuesto)), masImpuesto: true, total: precioRedondo(centimos) }
    : { importe: precioRedondo(centimos), masImpuesto: false, total: null }

/** `1900` → `$19`; con el impuesto aparte, `$19 + impuesto`. Para las frases y las pastillas. */
export const precioEnUnaLinea = (centimos: number, impuesto: ImpuestoDeCobro): string => {
  const p = precioVisible(centimos, impuesto)
  return p.masImpuesto ? `${p.importe} + impuesto` : p.importe
}

// --- Lo que incluye un plan ---------------------------------------------------------------

const NOMBRE_DE_MODULO: Record<string, string> = {
  paginas: 'Páginas',
  entradas: 'Blog',
  formularios: 'Formularios',
  seo: 'SEO',
  productos: 'Tienda',
  servicios: 'Servicios',
  // Los tipos de contenido a medida («Propiedades», «Cursos»…) y sus taxonomías (plan del CMS dinámico §6).
  estructura: 'Tipos de contenido',
}

export const nombreDeModulo = (modulo: string): string => NOMBRE_DE_MODULO[modulo] ?? modulo.charAt(0).toUpperCase() + modulo.slice(1)

const conjuncion = new Intl.ListFormat('es', { style: 'long', type: 'conjunction' })

/** `['paginas', 'entradas', 'productos']` → «Páginas, Blog y Tienda». Sin repetir. */
export const listaDeModulos = (modulos: string[]): string => conjuncion.format([...new Set(modulos.map(nombreDeModulo))])

export const textoDeSitios = (maximo: number): string => (maximo > 1 ? `Hasta ${maximo.toLocaleString('es')} webs` : '1 web')

export const textoDeCreditos = (creditos: number): string => `${creditos.toLocaleString('es')} créditos de IA al mes`

export type AccionDePlan = { texto: string; deshabilitado: boolean; /** Lo que conviene saber antes de pulsar. */ nota?: string }

/**
 * El botón de cada plan: el que ya se paga no se vuelve a contratar; con otra suscripción
 * es un cambio (el núcleo cancela la anterior al activarse la nueva); si no, suscribirse.
 * Una suscripción suspendida del mismo plan se puede volver a contratar: el núcleo lo admite.
 * Una **cancelada que todavía cubre** se reactiva sin pagar antes de tiempo: PayPal empieza a
 * cobrar cuando termina lo pagado (el núcleo lo pide así).
 */
export const accionDelPlan = (plan: Pick<PlanALaVenta, 'id'>, suscripcion: null | (Pick<SuscripcionVigente, 'estado' | 'plan'> & Partial<Pick<SuscripcionVigente, 'hasta'>>)): AccionDePlan => {
  if (suscripcion?.plan === plan.id && suscripcion.estado === 'activa') return { texto: 'Tu plan', deshabilitado: true }
  if (suscripcion?.estado === 'cancelada') {
    const hasta = fechaLarga(suscripcion.hasta)
    if (suscripcion.plan === plan.id) return { texto: 'Reactivar mi plan', deshabilitado: false, nota: hasta ? `No se cobra nada hasta el ${hasta}, cuando termina lo que ya pagaste.` : undefined }
    return { texto: 'Suscribirme con PayPal', deshabilitado: false, nota: 'Empieza y se cobra en cuanto lo apruebes en PayPal.' }
  }
  if (suscripcion && suscripcion.plan !== plan.id) return { texto: 'Cambiar a este plan', deshabilitado: false }
  return { texto: 'Suscribirme con PayPal', deshabilitado: false }
}

const RESULTADOS: Record<string, string> = {
  'rechazada-por-politica': 'no se aplicó: tocaba algo que no se puede cambiar',
  'fallo-de-validacion': 'no pasó la revisión del código',
  descartada: 'no salió ningún cambio',
  error: 'el proveedor de IA no respondió',
}

/**
 * Un movimiento del libro de créditos, dicho en llano (§28): «Apartado mientras se genera» y
 * «Devuelto lo apartado» en vez de «Reserva» y «Liquidación de la reserva», «versión 12» en vez
 * de «v12», y el motivo de una devolución sin la clave interna del resultado.
 */
export const movimientoEnLlano = (m: { tipo: string; motivo?: null | string; bolsa?: null | string }): { que: string; motivo: string } => {
  const bruto = m.motivo ?? ''
  const liquidacion = /^Liquidación de la reserva:\s*/.exec(bruto)
  let motivo = bruto.replace(/^Liquidación de la reserva:\s*/, '').replace(/\bv(\d+)\b/g, 'versión $1')
  const devolucion = /^Devolución:\s*([a-z-]+)$/.exec(motivo)
  if (devolucion && RESULTADOS[devolucion[1]!]) motivo = `Devolución: ${RESULTADOS[devolucion[1]!]}`
  motivo = motivo.replace(/^Reserva para /, 'Para ')
  const que =
    m.tipo === 'reserva'
      ? 'Apartado mientras se genera'
      : m.tipo === 'devolucion'
        ? liquidacion
          ? 'Devuelto lo apartado'
          : 'Devolución'
        : m.tipo === 'consumo'
          ? 'Gastado'
          : m.tipo === 'concesion'
            ? m.bolsa === 'bienvenida'
              ? 'Bienvenida'
              : 'Créditos del plan'
            : m.tipo === 'compra'
              ? 'Compra'
              : m.tipo === 'ajuste'
                ? 'Ajuste'
                : m.tipo
  return { que, motivo }
}

/** Qué se puede pedir a la IA de diseño con un plan: «secciones y páginas», «secciones, páginas y todo el sitio». */
export const textoDeAlcances = (alcances: null | string[] | undefined): null | string => {
  if (!alcances || alcances.length === 0) return null
  const nombres: Record<string, string> = { seccion: 'secciones', pagina: 'páginas', sitio: 'todo el sitio' }
  return `Diseño con IA: ${conjuncion.format(['seccion', 'pagina', 'sitio'].filter((a) => alcances.includes(a)).map((a) => nombres[a]!))}`
}

// --- Fechas -------------------------------------------------------------------------------

/** «28 de octubre de 2026», o `null` si no hay fecha (o no es una). */
export const fechaLarga = (iso: null | string | undefined): null | string => {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })
}

// --- La vuelta de PayPal ------------------------------------------------------------------

export type VueltaDePayPal =
  | { tipo: 'cancelado' }
  | { tipo: 'creditos'; orden: string }
  | { tipo: 'incompleta' }
  | { tipo: 'suscripcion'; suscripcion: string }

/** Los parámetros con los que PayPal devuelve al cliente, y que no se quedan en la barra. */
export const PARAMETROS_DE_PAYPAL = ['paypal', 'subscription_id', 'ba_token', 'token', 'PayerID'] as const

// Las mismas formas que acepta el núcleo: lo demás no es de PayPal y no se le manda.
const RE_ORDEN = /^[A-Z0-9]{8,40}$/
const RE_SUSCRIPCION = /^I-[A-Z0-9]{6,40}$/

/**
 * Qué trae la dirección al volver de PayPal: `?paypal=suscripcion&subscription_id=I-…`,
 * `?paypal=creditos&token=<orden>` o `?paypal=cancelado`. `null` si no se viene de PayPal;
 * `incompleta` si se viene pero falta el identificador (o no tiene la forma de uno).
 */
export const leerVueltaDePayPal = (params: URLSearchParams): null | VueltaDePayPal => {
  const paypal = params.get('paypal')
  if (paypal === null) return null
  if (paypal === 'cancelado') return { tipo: 'cancelado' }
  if (paypal === 'creditos') {
    const orden = params.get('token')?.trim() ?? ''
    return RE_ORDEN.test(orden) ? { tipo: 'creditos', orden } : { tipo: 'incompleta' }
  }
  if (paypal === 'suscripcion') {
    const suscripcion = params.get('subscription_id')?.trim() ?? ''
    return RE_SUSCRIPCION.test(suscripcion) ? { tipo: 'suscripcion', suscripcion } : { tipo: 'incompleta' }
  }
  return { tipo: 'incompleta' }
}

/** La misma dirección sin lo que puso PayPal; lo demás (una pestaña, un filtro) se queda. */
export const sinParametrosDePayPal = (params: URLSearchParams): URLSearchParams => {
  const limpios = new URLSearchParams(params)
  for (const clave of PARAMETROS_DE_PAYPAL) limpios.delete(clave)
  return limpios
}

/**
 * Solo se sale del panel hacia PayPal: si el núcleo devolviera otra cosa (un error de
 * configuración, un `javascript:`), no se sigue el enlace.
 */
export const esDireccionDePayPal = (direccion: string): boolean => {
  try {
    const url = new URL(direccion)
    return url.protocol === 'https:' && (url.hostname === 'paypal.com' || url.hostname.endsWith('.paypal.com'))
  } catch {
    return false
  }
}

// --- Qué se le dice al cliente ------------------------------------------------------------

export type TonoDeAviso = 'aviso' | 'error' | 'info' | 'ok'
export type Mensaje = { tono: TonoDeAviso; texto: string }

export const mensajeDeVuelta = (vuelta: VueltaDePayPal): Mensaje => {
  switch (vuelta.tipo) {
    case 'cancelado':
      return { tono: 'info', texto: 'Has vuelto de PayPal sin pagar: no se ha cobrado nada.' }
    case 'incompleta':
      return { tono: 'aviso', texto: 'No hemos podido leer la respuesta de PayPal. Si llegaste a pagar, lo verás aquí en unos minutos; si no, escríbenos.' }
    case 'creditos':
      return { tono: 'info', texto: 'Confirmando tu compra con PayPal…' }
    case 'suscripcion':
      return { tono: 'info', texto: 'Confirmando tu suscripción con PayPal…' }
  }
}

export const mensajeDeCaptura = (r: ResultadoDeCaptura): Mensaje => {
  if (r.creditos > 0) return { tono: 'ok', texto: `Listo: tienes ${r.creditos.toLocaleString('es')} créditos más.` }
  const detalle = r.detalle.trim()
  return { tono: 'aviso', texto: `Todavía no se han sumado los créditos.${detalle ? ` ${detalle}` : ''} Si en un rato no los ves, escríbenos.` }
}

export const ESPERANDO_A_PAYPAL = 'Estamos esperando la confirmación de PayPal; en unos minutos verás tu plan.'

export const mensajeDeConfirmacion = (estado: EstadoDeSuscripcion): Mensaje => {
  switch (estado) {
    case 'activa':
      return { tono: 'ok', texto: 'Tu suscripción está activa y los créditos del plan ya están en tu saldo. PayPal te cobrará cada mes.' }
    case 'pendiente':
      return { tono: 'info', texto: ESPERANDO_A_PAYPAL }
    case 'suspendida':
      return { tono: 'aviso', texto: 'PayPal no ha podido cobrar: revisa tu método de pago en PayPal.' }
    default:
      return { tono: 'aviso', texto: 'PayPal dice que esa suscripción ya no está activa, así que tu plan no ha cambiado. Puedes volver a suscribirte cuando quieras.' }
  }
}

// --- Hacerlo una vez ----------------------------------------------------------------------

type Almacen = Pick<Storage, 'getItem' | 'setItem'>

/** `sessionStorage`, si el navegador lo deja usar (en una ventana privada puede lanzar). */
const almacenDeLaSesion = (): Almacen | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

const claveDeCaptura = (orden: string) => `wixy-paypal-captura:${orden}`

const leerCaptura = (almacen: Almacen | null, orden: string): null | ResultadoDeCaptura => {
  try {
    const texto = almacen?.getItem(claveDeCaptura(orden))
    if (!texto) return null
    const valor = JSON.parse(texto) as Partial<ResultadoDeCaptura>
    return typeof valor.creditos === 'number' && typeof valor.detalle === 'string' ? { creditos: valor.creditos, detalle: valor.detalle } : null
  } catch {
    return null
  }
}

const guardarCaptura = (almacen: Almacen | null, orden: string, r: ResultadoDeCaptura) => {
  try {
    almacen?.setItem(claveDeCaptura(orden), JSON.stringify(r))
  } catch {
    /* Sin almacén se puede volver a pedir: el núcleo no cobra dos veces. */
  }
}

const capturasEnCurso = new Map<string, Promise<ResultadoDeCaptura>>()

/**
 * Cobrar la orden **una vez**. El efecto que la pide corre dos veces en StrictMode, y el
 * cliente puede recargar: lo que ya se cobró se lee de la sesión del navegador, y dos
 * llamadas a la vez comparten la misma petición. Si falla, se puede volver a intentar
 * (el núcleo, además, no cobra dos veces la misma orden).
 */
export const capturarUnaVez = (orden: string, capturar: (orden: string) => Promise<ResultadoDeCaptura>, almacen: Almacen | null = almacenDeLaSesion()): Promise<ResultadoDeCaptura> => {
  const guardada = leerCaptura(almacen, orden)
  if (guardada) return Promise.resolve(guardada)
  const enCurso = capturasEnCurso.get(orden)
  if (enCurso) return enCurso
  const promesa = capturar(orden).then((r) => {
    guardarCaptura(almacen, orden, r)
    return r
  })
  capturasEnCurso.set(orden, promesa)
  const olvidar = () => capturasEnCurso.delete(orden)
  promesa.then(olvidar, olvidar)
  return promesa
}

const confirmacionesEnCurso = new Map<string, Promise<EstadoDeSuscripcion>>()

export const dormir = (ms: number) => new Promise<void>((resolver) => window.setTimeout(resolver, ms))

/**
 * Preguntar al núcleo cómo quedó la suscripción, que a su vez le pregunta a PayPal. PayPal
 * a veces tarda en darla por activa: mientras diga `pendiente`, se vuelve a preguntar cada
 * `esperaMs`, hasta `intentos` veces. Dos llamadas a la vez comparten la misma espera.
 */
export const confirmarConEspera = (
  suscripcion: string,
  confirmar: (suscripcion: string) => Promise<EstadoDeSuscripcion>,
  { intentos = 5, esperaMs = 3000, esperar = dormir }: { esperaMs?: number; esperar?: (ms: number) => Promise<void>; intentos?: number } = {},
): Promise<EstadoDeSuscripcion> => {
  const enCurso = confirmacionesEnCurso.get(suscripcion)
  if (enCurso) return enCurso
  const promesa = (async () => {
    let estado = await confirmar(suscripcion)
    for (let intento = 1; intento < intentos && estado === 'pendiente'; intento += 1) {
      await esperar(esperaMs)
      estado = await confirmar(suscripcion)
    }
    return estado
  })()
  confirmacionesEnCurso.set(suscripcion, promesa)
  const olvidar = () => confirmacionesEnCurso.delete(suscripcion)
  promesa.then(olvidar, olvidar)
  return promesa
}
